import { useEffect } from 'react';
import BrandMark from '../components/BrandMark';
import { AuthShell, AUTH_TITLE_CLASS } from '@/components/auth/AuthShell';
import { cn } from '@/lib/utils';

// Public: the server's `Link: <…/docs/api-key-deprecation>; rel="sunset"`
// header points here from every /mcp request that puts its API key in the
// address (?key=). Before this page existed the link fell through to the app.

export default function ApiKeyDeprecationPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'API keys in the address are going away · Cadence';
    return () => { document.title = prev; };
  }, []);

  return (
    <AuthShell width="wide">
      <main>
        <BrandMark />
        <h1 className={cn('mt-8', AUTH_TITLE_CLASS)}>API keys in the address are going away</h1>
        <p className="mt-4 text-muted-foreground">
          You can connect a tool to Cadence’s MCP server by putting an API key in the address, like
          {' '}<code className="rounded bg-muted px-1 py-0.5 text-sm">https://api.stratiumhq.com/mcp?key=cad_k1_…</code>.
          That way of connecting is deprecated and will stop working. Addresses end up in browser history,
          server logs and proxies, so a key in one can be read by people who shouldn’t have it.
        </p>

        <h2 className="mt-8 text-lg font-semibold">What to do</h2>
        <ol className="mt-3 list-decimal space-y-3 pl-6 text-muted-foreground">
          <li>
            <span className="text-foreground">For Claude, use the Cadence connector.</span> It signs in with your Cadence
            account, so there’s no key to copy. Settings in Cadence shows how to add it.
          </li>
          <li>
            <span className="text-foreground">For your own tools, send the key in a header instead.</span> Use the address
            {' '}<code className="rounded bg-muted px-1 py-0.5 text-sm">https://api.stratiumhq.com/mcp</code> with the header
            {' '}<code className="rounded bg-muted px-1 py-0.5 text-sm">Authorization: Bearer cad_k1_…</code>.
          </li>
          <li>
            <span className="text-foreground">Then replace the key.</span> One that has been in an address should be treated
            as seen: create a new one in Cadence and revoke the old one.
          </li>
        </ol>

        <p className="mt-8 text-sm text-muted-foreground">
          Responses to requests that still use <code className="rounded bg-muted px-1 py-0.5">?key=</code> carry a
          {' '}<code className="rounded bg-muted px-1 py-0.5">Sunset</code> header with the planned date.
          {' '}<a href="/" className="text-primary hover:underline">Open Cadence</a>
        </p>
      </main>
    </AuthShell>
  );
}
